import './render-ai-expend-image.less';
import { useLatest } from 'ahooks';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { useEffect, useRef, useState } from 'react';
import { base64Kit } from '@meitu/saas-editor-core/es/utils/base64-kit';
import { loadImageAsync } from '@meitu/saas-editor-core/es/utils/file-kit';
import { IImageLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { Loading, message } from '@meitu/xdesign-widgets';
import gridIcon from '@/assets/bg/grid.png';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useStoreActions, useStoreState } from '@/store';
import { Entrance } from '@/types';
import { isFromSafari } from '@/utils/common';
import { openVipModal } from '@/utils/login';
import loadingImg from '../../../assets/AIRepair_00028.png';
import AIRepairIcon from '../../../assets/AIRepair.webp';
import { getImageExpendSize } from '../utils';
import CursorMoveTip from './cursor-move-tip/cursor-move-tip';
import useMoveImage from './use-move-image';
import useShowLayerWaterMark from './use-show-layer-watermark';

const RenderAIExpendImage = () => {
    const isAiExpendImage = useStoreState((state) => state.pluginModel.isAiExpendImage);

    const aiExpendImageInfo = useLatest(useStoreState((state) => state.pluginModel.aiExpendImageInfo));
    const [scale, setScale] = useState(1);
    const [wrapSize, setWrapSize] = useState({ width: 0, height: 0 });
    const [renderUrl, setRenderUrl] = useState('');
    const setAIExpendImageInfo = useStoreActions((actions) => actions.pluginModel.setAIExpendImageInfo);

    const renderAIExpendImageComponentRef = useRef<HTMLDivElement>(null);

    const { $t } = useLocaleStore();

    const [newSize, setNewSize] = useState({
        newWidth: 0,
        newHeight: 0,
    });

    const [natureImageSize, setNatureImageSize] = useState({
        naturalWidth: 0,
        naturalHeight: 0,
    });
    const { naturalWidth, naturalHeight } = natureImageSize;

    const showLayerWaterMark = useShowLayerWaterMark();

    const [showOriginImage, setShowOriginImage] = useState(false);

    useEffect(() => {
        const getSize = async () => {
            const activeLayer = editor.active.getPick(true) || editor.active.getMainLayer();
            if (!activeLayer) {
                return null;
            }
            Loading.show($t((v) => v.globalMask.loading));
            const { layerType, data } = activeLayer;
            const { url } = data as IImageLayerProps;
            if (![LayerType.MAIN_LAYER, LayerType.IMAGE_LAYER].includes(layerType)) {
                Loading.hidden();
                return null;
            }

            const renderUrl = base64Kit.getLocalUrl(url);
            setRenderUrl(renderUrl);
            const { width, height } = await loadImageAsync(renderUrl);
            setNatureImageSize({ naturalWidth: width, naturalHeight: height });

            Loading.hidden();
        };
        getSize();
    }, []);

    const offset = useMoveImage(scale, isAiExpendImage && !aiExpendImageInfo.current.resultUrl);

    useEffect(() => {
        let offsetX = aiExpendImageInfo.current.offsetX + offset.offsetX;
        let offsetY = aiExpendImageInfo.current.offsetY + offset.offsetY;

        const { newWidth, newHeight } = getImageExpendSize(naturalWidth, naturalHeight, aiExpendImageInfo.current);

        if (Math.abs(offsetX) > (newWidth - naturalWidth) / 2) {
            offsetX = offsetX > 0 ? (newWidth - naturalWidth) / 2 : -(newWidth - naturalWidth) / 2;
        }

        if (Math.abs(offsetY) > (newHeight - naturalHeight) / 2) {
            offsetY = offsetY > 0 ? (newHeight - naturalHeight) / 2 : -(newHeight - naturalHeight) / 2;
        }

        setAIExpendImageInfo({
            ...aiExpendImageInfo.current,
            offsetX,
            offsetY,
        });
    }, [offset]);

    useEffect(() => {
        const activeLayer = editor.active.getPick(true) || editor.active.getMainLayer();
        if (!activeLayer) {
            return;
        }

        const { data, layerType } = activeLayer;

        if (
            ![LayerType.MAIN_LAYER, LayerType.IMAGE_LAYER].includes(layerType) ||
            naturalWidth === 0 ||
            naturalHeight === 0
        ) {
            return;
        }

        const expendSize = getImageExpendSize(naturalWidth, naturalHeight, aiExpendImageInfo.current);
        setNewSize({
            newWidth: expendSize.newWidth,
            newHeight: expendSize.newHeight,
        });

        const scaleImg = (index = 0) => {
            const wrapElem = renderAIExpendImageComponentRef.current;
            if (!wrapElem) {
                return;
            }

            const { width: wrapWidth, height: wrapHeight } = wrapElem.getBoundingClientRect();
            if (wrapWidth === 0 || wrapHeight === 0) {
                // 如果宽高为0，则等待100ms后重新计算 不知道为什么经常有获取到dom宽高为0得情况
                if (index < 10) {
                    setTimeout(() => {
                        scaleImg(index + 1);
                    }, 50);
                }
                return;
            }
            setWrapSize({ width: wrapWidth, height: wrapHeight });
            const PADDING = 60;
            if (expendSize.newWidth <= wrapWidth - PADDING / 2 && expendSize.newHeight <= wrapHeight - PADDING / 2) {
                setScale(1);
                return;
            }

            const widthRadio = (wrapWidth - PADDING / 2) / expendSize.newWidth;
            const heightRadio = (wrapHeight - PADDING / 2) / expendSize.newHeight;

            if (widthRadio < heightRadio) {
                setScale(widthRadio);
            } else {
                setScale(heightRadio);
            }
        };
        const runScaleImg = () => {
            scaleImg(1);
        };
        window.addEventListener('resize', runScaleImg);
        scaleImg(1);

        return () => {
            window.removeEventListener('resize', runScaleImg);
        };
    }, [aiExpendImageInfo.current, naturalWidth, naturalHeight]);

    if (!isAiExpendImage) {
        return null;
    }
    const activeLayer = editor.active.getPick(true) || editor.active.getMainLayer();
    if (!activeLayer) {
        return null;
    }
    const { data, layerType } = activeLayer;

    if (![LayerType.MAIN_LAYER, LayerType.IMAGE_LAYER].includes(layerType)) {
        return null;
    }

    const { url } = data as IImageLayerProps;
    const { left, top } = getImageExpendSize(naturalWidth, naturalHeight, aiExpendImageInfo.current);

    const downloadImg = () => {
        if (showLayerWaterMark) {
            message.info($t((v) => v.header.openVipAndDownload));
            openVipModal({
                entrance: Entrance.Save,
            });
        } else {
            saveAs(
                aiExpendImageInfo.current.resultUrl,
                `${$t((v) => v.editor.copyRight)}_${
                    isIntl ? 'X-Design' : $t((v) => v.collapse.moreCollapse.imageTool.aiExpend.title)
                }_${dayjs().format('YYYY.MM.DD')}.jpg`,
            );
            window._stat_track('aisc_download_success', {
                func: 'image_expand',
                multiple: aiExpendImageInfo.current.multiple,
                ratio: aiExpendImageInfo.current.ratio,
                is_creative: !!aiExpendImageInfo.current.desc,
                download_type: 'direct',
            });
        }
    };

    return (
        <div ref={renderAIExpendImageComponentRef} className="c-renderAIExpendImage">
            <div className="bg"></div>
            <div
                className="img-warp"
                id="AIExpendImageContent"
                data-operate-type="AIExpendImageContent"
                style={{
                    width: `${newSize.newWidth}px`,
                    height: `${newSize.newHeight}px`,
                    transform: `translate(${(wrapSize.width - newSize.newWidth * scale) / 2}px, ${
                        (wrapSize.height - newSize.newHeight * scale) / 2
                    }px) scale(${scale})`,
                    backgroundSize: `${16 / scale}px ${16 / scale}px`,
                    backgroundImage:
                        aiExpendImageInfo.current.resultUrl && !showOriginImage ? 'none' : `url(${gridIcon})`,
                }}
            >
                {showLayerWaterMark ? <div className="watermark"></div> : null}
                <img
                    id="expendOperateImage"
                    src={renderUrl || url}
                    width={naturalWidth}
                    height={naturalHeight}
                    style={{
                        left: `${left}px`,
                        top: `${top}px`,
                        visibility: aiExpendImageInfo.current.resultUrl && !showOriginImage ? 'hidden' : 'visible',
                        border: `${2 / scale}px solid var(--stroke-dropAreaTransparentHover, #66A8FF)`,
                    }}
                    alt="img"
                />

                {aiExpendImageInfo.current.resultUrl && !showOriginImage ? (
                    <div
                        className="showResult"
                        style={{
                            backgroundImage: `url(${aiExpendImageInfo.current.resultUrl})`,
                        }}
                    ></div>
                ) : null}
            </div>

            {aiExpendImageInfo.current.loading ? (
                <div className="loading-wrap">
                    <div className="loading">
                        <img src={isFromSafari() ? loadingImg : AIRepairIcon} alt="loading" />
                        <span>{$t((v) => v.stage.AIExpendImage)}...</span>
                    </div>
                </div>
            ) : null}

            <CursorMoveTip
                hide={!!(aiExpendImageInfo.current.loading || aiExpendImageInfo.current.resultUrl)}
                text={$t((v) => v.stage.modifyImageTip)}
                elemId="AIExpendImageContent"
            />

            {aiExpendImageInfo.current.resultUrl ? (
                <div className="resultBtnGroup">
                    <div className="downloadResult" onClick={downloadImg}>
                        <IconFont name="DownloadBold" color="#fff" fontSize={16} />
                        <span>{$t((v) => v.stage.downloadResultBtnText)}</span>
                    </div>

                    <div
                        className="compare"
                        onMouseDown={() => setShowOriginImage(true)}
                        onMouseUp={() => setShowOriginImage(false)}
                    >
                        <IconFont name="CompareBold" color="#fff" fontSize={16} />
                        <span>{$t((v) => v.stage.contrast)}</span>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default RenderAIExpendImage;
